import { useEffect } from 'react';
import { areSearchStringsEqual, mergeSearchParams } from '@/utils/urlSearch';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';

/**
 * Generic, reusable URL-synced filter state for report-style pages (member,
 * date range, timezone). Every piece below is single-responsibility and
 * opt-in — a page composes only the ones it actually has a control for
 * (e.g. a page with no member dropdown just skips the member hooks).
 *
 * Design notes (why this looks the way it does):
 *
 * - No page here needs `history.listen` for member/date sync. `useLocation()`
 *   already re-renders the component on every navigation (POP, PUSH, or a
 *   REPLACE from our own writer), so a plain `useEffect` keyed on
 *   `location.search` reacts to all of them — simpler than manually
 *   listening and filtering by action type.
 * - Reads and writes are deliberately separate. `useUrlDateRangeSync` /
 *   `useUrlMemberHydrationSync` / `useUrlMemberNavSync` only ever call
 *   `setState` — never `history.replace` — because `params` for the one
 *   writer (`useUrlFilterWrite`) is built during the *render* phase, before
 *   any of this render's effects have run; if two of these sync hooks each
 *   independently wrote to the URL, the second write would be built from a
 *   `location.search` that doesn't yet reflect the first write, clobbering
 *   it. Routing every field through exactly one `useUrlFilterWrite` call per
 *   page avoids that race by construction — there's only ever one writer.
 * - Member sync is deliberately split into two hooks with different
 *   dependencies, not one combined effect: `useUrlMemberHydrationSync` only
 *   acts while nothing is selected yet (dormant afterwards, so a background
 *   roster refetch after that point is a no-op), while `useUrlMemberNavSync`
 *   only reacts to an actual `location.search` change (via a ref to the
 *   options list, not the live array) so it can't be re-triggered by an
 *   unrelated roster refetch clobbering a just-made manual pick.
 * - Timezone is NOT handled here. `useMemberCompanyTimezones` (a separate,
 *   unrelated hook) already owns "auto-follow the selected member's own
 *   timezone unless the user overrode it" — genuinely subtle, already
 *   correct, and orthogonal to URL persistence. The one small piece that
 *   *is* URL-related — resetting the "ignore the URL's tz" flag when an
 *   external navigation brings in a fresh `tz` — is exported below as
 *   `useUrlTimezoneIgnoreFlagSync`, and it's the one place that keeps
 *   `history.listen`, because it needs to tell apart "the writer just
 *   echoed the current tz back" from "the user navigated to a URL with a
 *   genuinely different tz" — a distinction `location.search` alone can't
 *   expose (it only exposes the resulting location, never *why* it changed).
 */

/**
 * Keeps a `{ start, end }` date range in sync with `?start`/`?end`.
 * Reactive and listener-free: re-derives on every `location.search` change
 * (browser back/forward, an inbound link with different dates, or our own
 * writer's self-echo) and only calls `setDateRange` when the result actually
 * differs — returns the same object reference otherwise, which is what
 * keeps this from cascading into a render loop.
 *
 * @param {{ location: import('history').Location, setDateRange: Function }} args
 */
export function useUrlDateRangeSync({ location, setDateRange }) {
  useEffect(() => {
    const next = getInitialReportDateRangeFromSearch(location.search);
    setDateRange((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next
    );
  }, [location.search, setDateRange]);
}

/**
 * A stable, pure default-member picker: the currently signed-in user if
 * they're on the roster, else the first available option. Exported as a
 * plain function (not created inline per page) so its identity never
 * churns across renders — pass it straight into
 * {@link useUrlMemberHydrationSync}'s `getDefaultMember`.
 *
 * @param {Array<{ value: string }>} membersOptions
 * @param {string} [currentUserId]
 */
export function getDefaultMemberFromOptions(membersOptions, currentUserId) {
  return (
    membersOptions.find((m) => m.value === currentUserId) ||
    membersOptions[0] ||
    null
  );
}

/**
 * Resolves `?member` (or `paramKey`, for reusing this against a differently
 * named single-select field — e.g. Alerts' `?team`) against a loaded
 * options list and applies a default the *first* time there's nothing
 * valid selected — then goes dormant. Resets to `null` when `enabled`
 * becomes falsy (e.g. a team switch invalidating the current member).
 * Deliberately does NOT re-run once `selectedMember` is set, so an options
 * refetch landing after hydration can never clobber a fresh manual pick
 * (that class of race was caught in design review).
 *
 * @param {{
 *   location: import('history').Location,
 *   enabled?: boolean,
 *   membersOptions: Array<{ value: string }>,
 *   selectedMember: { value: string } | null,
 *   setSelectedMember: Function,
 *   currentUserId?: string,
 *   getDefaultMember: (options: Array<{ value: string }>, currentUserId?: string) => object | null,
 *   paramKey?: string,
 * }} args
 */
export function useUrlMemberHydrationSync({
  location,
  enabled = true,
  membersOptions,
  selectedMember,
  setSelectedMember,
  currentUserId,
  getDefaultMember,
  paramKey = 'member',
}) {
  useEffect(() => {
    if (!enabled) {
      setSelectedMember(null);
      return;
    }
    if (selectedMember) return;
    if (!membersOptions.length) return;

    const urlMemberId = getReportFiltersFromSearch(location.search)[paramKey];
    const fromUrl =
      urlMemberId && membersOptions.find((m) => m.value === urlMemberId);
    const resolved = fromUrl || getDefaultMember(membersOptions, currentUserId);
    if (resolved) setSelectedMember(resolved);
  }, [
    enabled,
    selectedMember,
    membersOptions,
    location.search,
    setSelectedMember,
    currentUserId,
    getDefaultMember,
    paramKey,
  ]);
}

/**
 * The browser back/forward half of member sync — deliberately split from
 * {@link useUrlMemberHydrationSync} so it never depends on the live
 * `membersOptions` array (only a ref to it, same pattern as the old code's
 * `membersOptionsRef`), so an unrelated options refetch can't retrigger it.
 * Only a genuine `location.search` change does.
 *
 * @param {{
 *   location: import('history').Location,
 *   enabled?: boolean,
 *   membersOptionsRef: { current: Array<{ value: string }> },
 *   setSelectedMember: Function,
 *   paramKey?: string,
 * }} args
 */
export function useUrlMemberNavSync({
  location,
  enabled = true,
  membersOptionsRef,
  setSelectedMember,
  paramKey = 'member',
}) {
  useEffect(() => {
    if (!enabled) return;
    const urlMemberId = getReportFiltersFromSearch(location.search)[paramKey];
    if (!urlMemberId) return;
    const opts = membersOptionsRef.current;
    if (!opts?.length) return;
    const fromUrl = opts.find((m) => m.value === urlMemberId);
    if (!fromUrl) return;
    setSelectedMember((prev) =>
      prev?.value === fromUrl.value ? prev : fromUrl
    );
  }, [
    location.search,
    enabled,
    membersOptionsRef,
    setSelectedMember,
    paramKey,
  ]);
}

/**
 * Resets the "ignore the URL's tz" flag when an external navigation
 * (browser back/forward, or an inbound link) brings in a fresh `tz` value.
 * Keeps `history.listen` deliberately — this is the one case where the
 * navigation *action* itself is load-bearing (see the module doc comment
 * above), not just the resulting location, so it can't be replaced by a
 * `location.search`-reactive effect like the hooks above.
 *
 * @param {{
 *   history: import('history').History,
 *   setIgnoreUrlTimezone: Function,
 *   pathnames?: Set<string>,
 * }} args
 */
export function useUrlTimezoneIgnoreFlagSync({
  history,
  setIgnoreUrlTimezone,
  pathnames,
}) {
  useEffect(() => {
    return history.listen((nextLocation, action) => {
      if (pathnames && !pathnames.has(nextLocation.pathname)) return;
      if (action !== 'POP' && action !== 'PUSH') return;
      const { tz } = getReportFiltersFromSearch(nextLocation.search || '');
      if (tz) setIgnoreUrlTimezone(false);
    });
  }, [history, setIgnoreUrlTimezone, pathnames]);
}

/**
 * The single atomic write path — every filter on a page should funnel
 * through exactly one call to this per render. Merges `params` into
 * `location.search` via one `history.replace`, preserving unrelated keys
 * (flow params like `cid`), and skips the replace entirely when nothing
 * actually changed (avoids replace loops).
 *
 * IMPORTANT: omit a key entirely from `params` (don't pass `''`) while its
 * value is still unresolved (e.g. `selectedMember` is `null` before the
 * roster loads) — `mergeSearchParams` treats an absent key as "leave
 * whatever's already there," so omission preserves a deep-linked value
 * through the async resolution window. Passing `''` would actively erase
 * it (caught in design review — a real regression on shared/refreshed
 * links if got wrong).
 *
 * @param {{
 *   history: import('history').History,
 *   location: import('history').Location,
 *   params: Record<string, string | undefined>,
 *   enabled?: boolean,
 * }} args
 */
export function useUrlFilterWrite({
  history,
  location,
  params,
  enabled = true,
}) {
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return;
    const nextSearch = mergeSearchParams(location.search, params);
    if (areSearchStringsEqual(location.search, nextSearch)) return;
    history.replace({ pathname: location.pathname, search: nextSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, history, location.pathname, location.search, paramsKey]);
}
